import { log } from '../app/logger.js';
import { HTMLControl } from './html_control.js';
import './popup_control.js';

/**
 * Base class to create widgets having a popup with a list of items, for
 * example, autocomplete controls or popup buttons.
 */
class PopupListControl extends HTMLControl {
  constructor() {
    super();

    this._selected = null;

    this.waitForUserInputEnd = false;
    this.searching = false;
    this.restartSearch = false;
    this.ignoreBlur = false;

    this.itemsData = [];
  }

  initialize() {
    super.initialize();

    this.datalist = this.querySelector('popup-control');
    this.datalistItems = this.datalist.contentElement;
    this.datalistItems.classList.add('popup-list');
    this.datalist.addEventListener(
      'mousedown',
      this.onListMousedown.bind(this),
      false
    );
    this.datalist.addEventListener('click', this.onListClick.bind(this), false);
  }

  static get markup() {
    return `
<span class='popup-list-box'>${this.boxMarkup}</span>
<popup-control>
`;
  }

  static get boxMarkup() {
    return '';
  }

  get open() {
    return this.datalist.open;
  }
  set open(val) {
    // Do not open if no items.
    this.datalist.open = val && this.itemsData.length > 0;
  }

  closeIfOpen() {
    if (!this.open) {
      return false;
    }
    this.open = false;
    return true;
  }

  // Implementation
  acceptSelected(ev) {
    this.open = false;

    this.value = this.selectedData.name;

    let itemSelectEv = new CustomEvent('itemselect', {
      detail: this.selectedData,
    });
    itemSelectEv.preventDefault = () => ev?.preventDefault();
    this.dispatchEvent(itemSelectEv);
  }

  get selected() {
    return this._selected;
  }
  set selected(val) {
    if (this._selected) {
      this._selected.removeAttribute('selected');
    }
    this._selected = val;
    if (this._selected) {
      this._selected.setAttribute('selected', 'true');
    }
  }

  get selectedIndex() {
    let idx = 0;
    let el = this.selected;
    if (!el) {
      return -1;
    }
    while ((el = el.previousElementSibling)) {
      idx++;
    }
    return idx;
  }

  set selectedIndex(v) {
    let idx = v;
    let el = this.datalistItems.firstElementChild;
    while (idx > 0 && (el = el.nextElementSibling)) {
      idx--;
    }
    this.selected = el;
  }

  get selectedData() {
    let idx = this.selectedIndex;
    if (idx == -1) {
      return null;
    }
    return this.itemsData[idx];
  }

  scrollSelectedIntoView() {
    if (this.selected) {
      let item_top = this.selected.offsetTop - this.datalistItems.offsetTop;
      let item_bottom = item_top + this.selected.clientHeight;

      let scroll_down_offset =
        item_bottom -
        this.datalistItems.scrollTop -
        this.datalistItems.clientHeight;
      if (scroll_down_offset > 0) {
        this.datalistItems.scrollTop += scroll_down_offset;
        return;
      }

      let scroll_up_offset = item_top - this.datalistItems.scrollTop;
      if (scroll_up_offset < 0) {
        this.datalistItems.scrollTop += scroll_up_offset;
      }
    }
  }

  setItems(items) {
    this.itemsData = items;

    let markup = '';
    for (let item of items) {
      markup += this.getItemUI(item);
    }
    this.datalistItems.innerHTML = markup;
  }

  getItemUI(item) {
    if (item.markup) {
      return item.markup;
    }

    let className = item.class ? ` ${item.class}` : '';
    let titleAttr = item.title ? `title='${item.title}'` : '';
    let typeAttr = item.type ? `type='${item.type}'` : '';
    let imgMarkup = item.img ? `<img src='${item.img}'>` : '';
    let name = item.extendedName || item.name;
    return `
      <div class='popup-list-item${className}' ${titleAttr} ${typeAttr}>
        ${imgMarkup}
        <span class='popup-list-item-text'>${name}</span>
      </div>
    `;
  }

  onBlur(ev) {
    if (this.ignoreBlur) {
      this.ignoreBlur = false;
      return;
    }

    this.open = false;

    if (this._onblur) {
      this._onblur(ev);
    }
  }

  onKeyDown(ev) {
    switch (ev.key) {
      case 'ArrowDown':
        if (this.open) {
          if (this.selected) {
            if (this.selected.nextElementSibling) {
              this.selected = this.selected.nextElementSibling;
            } else {
              this.selected = null;
            }
          } else {
            this.selected = this.datalistItems.firstElementChild;
          }
          this.scrollSelectedIntoView();
          ev.preventDefault();
        }
        break;

      case 'ArrowUp':
        if (this.open) {
          if (this.selected) {
            if (this.selected.previousElementSibling) {
              this.selected = this.selected.previousElementSibling;
            } else {
              this.selected = null;
            }
          } else {
            this.selected = this.datalistItems.lastElementChild;
          }
          this.scrollSelectedIntoView();
          ev.preventDefault();
        }
        break;

      case 'ArrowLeft':
      case 'ArrowRight':
        if (this.open) {
          this.open = false;
        }
        break;

      case 'Escape':
        if (this.open) {
          this.open = false;
          ev.stopPropagation();
          ev.preventDefault();
        }
        break;

      case 'Enter':
        log(
          `[control:popuplist:event] keydown Enter open: ${this.open}, selected: ${this.selected}`
        );
        if (this.open && this.selected) {
          this.acceptSelected();
          ev.preventDefault();
        }
        break;

      default:
        break;
    }
  }

  onListMousedown(ev) {
    this.ignoreBlur = true;

    let el = ev.target;
    while (el && el.parentNode != this.datalistItems) {
      el = el.parentNode;
    }
    if (el) {
      this.selected = el;
    }
  }

  onListClick(ev) {
    let el = ev.target;
    while (el && el.parentNode != this.datalistItems) {
      el = el.parentNode;
    }
    if (el == this.selected) {
      this.acceptSelected(ev);
    }
  }

  static get selectors() {
    const Popup = 'popup-control';
    const AllItems = `${Popup} .popup-list-item`;
    const SelectedItem = `${AllItems}[selected]`;
    return {
      AllItems,
      Box: '.popup-list-box',
      Popup,
      SelectedItem,
      SelectedItemImage: `${SelectedItem} img`,
      ItemAt(i) {
        return `${AllItems}:nth-child(${i})`;
      },
    };
  }

  static get elements() {
    let s = this.selectors;
    return {
      boxElement: s.Box,
      selectedItemImage: s.SelectedItemImage,
    };
  }
}

export { PopupListControl };
